import { withTranslation } from 'react-i18next';
import { constant, util } from '@helper';
import { Promise } from 'bluebird';
import React from 'react';
import ReloadIcon from '@rsuite/icons/Reload';
import _ from 'lodash';
import {
  FaHome,
  FaBuilding,
  FaSuitcase,
  FaFileAlt,
  FaMagic,
  FaUser,
  FaEnvelope,
  FaDownload,
  FaSearch,
  FaEye,
  FaClock,
  FaCopy,
} from 'react-icons/fa';

import {
  Col,
  Affix,
  Stack,
  IconButton,
  FlexboxGrid,
  Grid,
  Row,
  Button,
  InputGroup,
  Input,
  SelectPicker,
  toaster,
  Table,
  Pagination,
  Divider,
  Heading,
  HeadingGroup,
  Modal,
  Text,
} from 'rsuite';
import { Icon } from '@rsuite/icons';
import { DocumentAPI, InvoiceAPI } from '@services/api';
import { StatusButton } from '@common/components';
import moment from 'moment';
import AuthStorage from '@common/services/authStorage';

const { withRouter } = util;

type State = {
  loading: boolean;
  currentPage: number;
  currentLimit: number;
  keyword: string;
  status: string;
  updatedAt: string;
  data: Array<any>;
  total: number;

  showPendingModal?: boolean;
};

class CandidateMyCVMakerScreen extends React.Component<any, State> {
  constructor(props: any) {
    super(props);

    this.state = {
      loading: false,
      currentPage: 1,
      currentLimit: 10,
      updatedAt: moment().format('DD MMM YYYY, HH:mm:ss'),
      keyword: '',
      status: '',
      data: [],
      total: 0,
    };
  }

  async componentDidMount(): Promise<void> {
    // eslint-disable-next-line global-require
    require('./styles.scss');

    await Promise.allSettled([
      new Promise((resolve, reject) => {
        try {
          this.getInvoices();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),
    ]);
  }

  componentDidUpdate(
    _prevProps: Readonly<any>,
    prevState: Readonly<State>,
  ): void {
    let needRefresh = false;
    if (prevState.status !== _.get(this, 'state.status')) {
      needRefresh = true;
    }

    if (prevState.keyword !== _.get(this, 'state.keyword')) {
      needRefresh = true;
    }

    if (needRefresh) {
      this.getInvoices();
    }
  }

  private redirectTo = (url: string): void => {
    const { navigate } = this.props;
    navigate(url, { replace: true });

    window.location.reload();
  };

  private getInvoices = async (): Promise<void> => {
    try {
      this.setState({ loading: true });

      const { currentPage, currentLimit, keyword, status } = this.state;
      const response = await InvoiceAPI.list(
        currentPage,
        currentLimit,
        keyword,
        status,
      );
      const data = _.get(response, 'data.data') || [];
      const total = Number(_.get(response, 'data.meta.total') || 0);

      this.setState({
        data,
        total,
        updatedAt: moment().format('DD MMM YYYY, HH:mm:ss'),
      });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    } finally {
      this.setState({
        loading: false,
      });
    }
  };

  private downloadCV = async (filename: string): Promise<void> => {
    try {
      await DocumentAPI.downloadFile(filename);
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private sendInvoiceToEmail = async (invoiceNumber: string): Promise<void> => {
    try {
      await InvoiceAPI.sendInvoiceToEmail(invoiceNumber);

      const user = AuthStorage.getInstance().getUser();
      toaster.push(
        util.buildMessage(
          `Successfully sent the resume PDF to your email: ${_.get(
            user,
            'email',
          )}`,
        ),
      );
    } catch (e: any) {
      toaster.push(
        util.buildErrorMessage(_.get(e, 'response.data.message') || e.message),
      );
    }
  };

  private renderPendingModal = ({ t } = this.props): React.ReactNode | null => (
    <Modal
      size="md"
      className="cv-maker-payment-modal"
      open={_.get(this.state, 'showPendingModal')}
      onClose={() => {
        this.setState({ showPendingModal: false });
      }}
    >
      <Modal.Body>
        <Stack direction="column" spacing={24}>
          <Stack.Item>
            <img src="/icon-cvmaker-pending-payment.png" alt="logo-pending" />
          </Stack.Item>
          <Stack.Item>
            <HeadingGroup className="cvmaker-title-header">
              <Heading>
                {t(constant.translation.cvMaker.pendingPaymentTitle)}
              </Heading>
              <Text muted>
                {t(constant.translation.cvMaker.pendingPaymentSubTitle)}
              </Text>
            </HeadingGroup>
          </Stack.Item>
          <Stack.Item>
            <Button
              className="btn-expire"
              appearance="ghost"
              startIcon={<FaClock />}
            >
              {'Expire in: '}
              <b style={{ marginLeft: '.5em' }}>hh:mm:ss</b>
            </Button>
          </Stack.Item>
          <Stack.Item>
            <Divider />
          </Stack.Item>
          <Stack.Item className="w-100">
            <Stack direction="row" justifyContent="space-between">
              <Stack.Item>
                <Stack
                  className="payment-details"
                  direction="column"
                  alignItems="flex-start"
                >
                  <p>Virtual Account</p>
                  <p className="subtitle">
                    [Virtual Account Number] <IconButton icon={<FaCopy />} />
                  </p>
                </Stack>
              </Stack.Item>
              <Stack.Item>
                <Stack
                  className="payment-details"
                  direction="column"
                  alignItems="flex-start"
                >
                  <p>Total Payment</p>
                  <p className="subtitle">
                    IDR 1.000.000,- <IconButton icon={<FaCopy />} />
                  </p>
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
          <Stack.Item>
            <Divider />
          </Stack.Item>
          <Stack.Item>
            <div className="note-container">
              <p>
                - Once your payment is confirmed, we'll email your CV and
                invoice to you.
              </p>
              <p>
                - If you encounter any issues, feel free to reach out to us at
                [email TG]. We're happy to help!
              </p>
            </div>
          </Stack.Item>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Stack
          spacing={16}
          className="w-100"
          justifyContent="flex-end"
          direction="row"
        >
          <Stack.Item>
            <Button
              appearance="subtle"
              className="btn-tg-fortier"
              onClick={() => this.setState({ showPendingModal: false })}
            >
              {t(constant.translation.general.formCancelButtonKey)}
            </Button>
          </Stack.Item>
          <Stack.Item>
            <Button
              className="next-btn"
              appearance="primary"
              onClick={() => null}
              startIcon={<ReloadIcon />}
            >
              {t(constant.translation.general.formRefreshStatButtonKey)}
            </Button>
          </Stack.Item>
        </Stack>
      </Modal.Footer>
    </Modal>
  );

  renderSideTabs = ({ location } = this.props): JSX.Element => (
    <Col lg={5} md={5} sm={0} xs={0}>
      <Affix className="tg-affix-side-panel" top={window.innerHeight * 0.15}>
        <Stack spacing={6} direction="column">
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/home')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/home')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaHome />}
          >
            Home
          </IconButton>
          <IconButton
            onClick={() =>
              this.redirectTo('/dashboard/candidate/search-companies')
            }
            appearance="subtle"
            className={
              _.isEqual(
                location.pathname,
                '/dashboard/candidate/search-companies',
              )
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaBuilding />}
          >
            Company
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/search-jobs')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/search-jobs')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaSuitcase />}
          >
            Search Job
          </IconButton>
          <IconButton
            onClick={() =>
              this.redirectTo('/dashboard/candidate/my-application')
            }
            appearance="subtle"
            className={
              _.isEqual(
                location.pathname,
                '/dashboard/candidate/my-application',
              )
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaFileAlt />}
          >
            My Application
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/my-cv-maker')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/my-cv-maker')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaMagic />}
          >
            My CV Maker
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/profile')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/profile')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaUser />}
          >
            Profile
          </IconButton>
        </Stack>
      </Affix>
    </Col>
  );

  renderMainContent = (): JSX.Element | null => {
    const { loading, data, total, currentLimit, currentPage, updatedAt } =
      this.state;

    return (
      <Col md={24} sm={24} xs={24} lg={19} className="my-cv-maker-main-content">
        <FlexboxGrid className="w-100">
          <FlexboxGrid.Item className="w-100" as={Col}>
            <Stack alignItems="center" justifyContent="space-between">
              <h1>My CV Maker</h1>
              {/* <Stack spacing={16}>
                <Button
                  appearance="subtle"
                  className="btn-tg-fortier d-inline"
                  onClick={this.sendCVToEmail}
                  startIcon={<Icon as={FaEnvelope} />}
                >
                  {t(constant.translation.general.formSendToEmailButtonKey)}
                </Button>
                <Button
                  appearance="primary"
                  className="btn-tg-primary d-inline"
                  onClick={() => null}
                  startIcon={<Icon as={FaDownload} />}
                >
                  {t(
                    constant.translation.general
                      .formDownloadInvoiceAndCVButtonKey,
                  )}
                </Button>
              </Stack> */}
            </Stack>
            <Stack>
              <div className="d-flex">
                <p className="subtitle">Last update at {updatedAt}</p>
                <IconButton
                  onClick={() => this.getInvoices()}
                  appearance="subtle"
                  className="float-right mt-4 ml-4"
                  icon={<ReloadIcon />}
                />
              </div>
            </Stack>
          </FlexboxGrid.Item>
          <FlexboxGrid.Item className="w-100 mt-8" as={Col}>
            <Stack justifyContent="space-between">
              <Stack.Item className="w-40 no-margin-auto">
                <InputGroup>
                  <InputGroup.Button>
                    <FaSearch />
                  </InputGroup.Button>
                  <Input
                    name="search"
                    placeholder="Search by invoice number..."
                    onChange={(keyword: string) => this.setState({ keyword })}
                  />
                </InputGroup>
              </Stack.Item>
              <Stack.Item className="w-15 no-margin-auto">
                <SelectPicker
                  data={_.map(
                    [
                      {
                        name: 'All',
                        value: '',
                      },
                      {
                        name: 'Success',
                        value: 'success',
                      },
                      {
                        name: 'Failed',
                        value: 'failed',
                      },
                      {
                        name: 'Pending',
                        value: 'pending',
                      },
                    ],
                    (l) => ({
                      label: _.get(l, 'name'),
                      value: _.get(l, 'value'),
                    }),
                  )}
                  searchable={false}
                  onSelect={(value) => this.setState({ status: value })}
                  onClean={() => this.setState({ status: '' })}
                  placeholder="Choose the status"
                />
              </Stack.Item>
            </Stack>
            <Stack
              alignItems="flex-start"
              className="mt-4"
              spacing={16}
              direction="column"
            >
              <Button
                appearance="primary"
                className="btn-tg-primary d-inline mt-2"
                onClick={() => this.redirectTo('/cv-maker')}
                startIcon={<Icon as={FaFileAlt} />}
              >
                {' '}
                Build new CV
              </Button>
              <Stack.Item className="w-100 mt-2">
                <Table
                  autoHeight
                  loading={loading}
                  rowHeight={() => 80}
                  data={data}
                  bordered
                  headerHeight={80}
                  onRowClick={() => null}
                >
                  <Table.Column flexGrow={1} align="center">
                    <Table.HeaderCell>Invoice Number</Table.HeaderCell>
                    <Table.Cell>
                      {(rowData) => {
                        return rowData.number;
                      }}
                    </Table.Cell>
                  </Table.Column>
                  <Table.Column flexGrow={1} align="center">
                    <Table.HeaderCell>Created At</Table.HeaderCell>
                    <Table.Cell>
                      {(rowData) => {
                        return rowData.createdAt;
                      }}
                    </Table.Cell>
                  </Table.Column>
                  <Table.Column flexGrow={1} align="center">
                    <Table.HeaderCell>CV File</Table.HeaderCell>
                    <Table.Cell>
                      {(rowData) => {
                        return rowData.file && rowData.status === 'success' ? (
                          <Button
                            appearance="link"
                            onClick={() =>
                              window.open(_.get(rowData, 'file.url'))
                            }
                            className="btn-tg-link-btn"
                          >
                            <FaEye className="mr-2" />
                            View
                          </Button>
                        ) : (
                          '-'
                        );
                      }}
                    </Table.Cell>
                  </Table.Column>
                  <Table.Column flexGrow={1} align="center">
                    <Table.HeaderCell>Price</Table.HeaderCell>
                    <Table.Cell>
                      {(rowData) => {
                        return util.formatCurrency(
                          _.get(rowData, 'price', 0) +
                            _.get(rowData, 'adminPrice', 0),
                        );
                      }}
                    </Table.Cell>
                  </Table.Column>
                  <Table.Column flexGrow={1} align="center">
                    <Table.HeaderCell>Payment Status</Table.HeaderCell>
                    <Table.Cell>
                      {(rowData) => <StatusButton variant={rowData.status} />}
                    </Table.Cell>
                  </Table.Column>
                  <Table.Column flexGrow={1} align="center">
                    <Table.HeaderCell>Action</Table.HeaderCell>
                    <Table.Cell>
                      {(rowData) => {
                        if (rowData.status === 'success') {
                          return (
                            <Stack spacing={16}>
                              <Button
                                appearance="subtle"
                                className="btn-tg-fortier
                                btn-tg-icon-only d-inline"
                                onClick={() =>
                                  this.sendInvoiceToEmail(rowData.number)
                                }
                                startIcon={<Icon as={FaEnvelope} />}
                              />
                              <Button
                                appearance="primary"
                                className="btn-tg-primary
                                btn-tg-icon-only d-inline"
                                onClick={() =>
                                  this.downloadCV(
                                    _.get(rowData, 'file.filename'),
                                  )
                                }
                                startIcon={<Icon as={FaDownload} />}
                              />
                            </Stack>
                          );
                        }

                        if (rowData.status === 'pending') {
                          return (
                            <Button
                              appearance="subtle"
                              className="btn-tg-fortier d-inline"
                              onClick={() =>
                                this.setState({ showPendingModal: true })
                              }
                              style={{ cursor: 'pointer' }}
                            >
                              Pay Now
                            </Button>
                          );
                        }

                        return '';
                      }}
                    </Table.Cell>
                  </Table.Column>
                </Table>
                <Pagination
                  prev
                  next
                  first
                  last
                  ellipsis
                  boundaryLinks
                  maxButtons={5}
                  className="mt-4"
                  size="xs"
                  layout={['total', '-', '|', 'pager']}
                  total={total}
                  limit={currentLimit}
                  activePage={currentPage}
                  onChangePage={(page: number) =>
                    this.setState({ currentPage: page })
                  }
                />
              </Stack.Item>
            </Stack>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </Col>
    );
  };

  render(): null | JSX.Element {
    return (
      <>
        {this.renderPendingModal()}
        <Grid className="home-static-authorized-container" fluid>
          <Row>
            {this.renderSideTabs()}
            {this.renderMainContent()}
          </Row>
        </Grid>
      </>
    );
  }
}

export default withRouter(withTranslation()(CandidateMyCVMakerScreen));
